import { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useInternalLinkChecker } from './useInternalLinkChecker';
import { useProgramPathStripper } from '../common/use-program-path-stripper';

// Helper function to check if a URL is a relative path
const isRelativePath = (url: string) => {
  return url.startsWith('/') || url.startsWith('./') || url.startsWith('../');
};

// Helper function to normalize URLs that might be missing protocols
const normalizeUrl = (rawUrl: string): URL => {
  // If it looks like a domain without protocol, add https://
  if (
    /^[a-zA-Z0-9][a-zA-Z0-9-_.]*\.[a-zA-Z]{2,}/.test(rawUrl) &&
    !rawUrl.includes('://')
  ) {
    return new URL(`https://${rawUrl}`);
  }

  // Try to parse as-is - this will handle absolute URLs and relative paths
  return new URL(rawUrl, window.location.origin);
};

/**
 * A hook that provides click and keyboard event handlers for links.
 * Handles internal vs external link navigation automatically.
 *
 * @param url - The URL to navigate to
 * @returns An object containing click and keydown handlers
 */
export const useLinkClickHandler = (url?: string) => {
  const history = useHistory();
  const { isInternalLink } = useInternalLinkChecker();

  // Strip program and brand slugs from the path before pushing to history
  // React Router will automatically prepend them based on the basename
  const stripProgramPath = useProgramPathStripper();

  // Get the normalized URL for use in href attributes
  const normalizedUrl = useMemo(() => {
    if (!url) return undefined;

    try {
      return normalizeUrl(url).href;
    } catch (error) {
      // If normalization fails, return the original URL
      return url;
    }
  }, [url]);

  // Helper function to check if we should handle the navigation internally
  const shouldHandleInternally = useCallback(
    (url: string): boolean => {
      if (isRelativePath(url)) {
        return true;
      }

      try {
        const urlObject = normalizeUrl(url);
        return isInternalLink(urlObject);
      } catch (error) {
        console.warn('Invalid URL for navigation:', url);
        return false;
      }
    },
    [isInternalLink]
  );

  const navigate = useCallback(() => {
    if (!url) return;

    // Check if it's a relative path - these should always be treated as internal
    if (isRelativePath(url)) {
      // For relative paths, use React Router directly
      const strippedPath = stripProgramPath(url);
      history.push(strippedPath);
      return;
    }

    try {
      const urlObject = normalizeUrl(url);

      if (isInternalLink(urlObject)) {
        // For internal links, use React Router to navigate
        const fullPath = urlObject.pathname + urlObject.search + urlObject.hash;
        const strippedPath = stripProgramPath(fullPath);
        history.push(strippedPath);
      } else {
        // For external links, navigate using window.open
        window.open(urlObject.href, '_self');
      }
    } catch (error) {
      // If URL parsing fails, treat as external link
      console.warn('Invalid URL for navigation:', url);
      if (url) {
        window.open(url, '_self');
      }
    }
  }, [url, isInternalLink, history, stripProgramPath]);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (!url) return;

      if (shouldHandleInternally(url)) {
        e.preventDefault();
        navigate();
      }
      // For external links, let the default anchor behavior handle it
    },
    [url, shouldHandleInternally, navigate]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLAnchorElement>) => {
      // Handle Enter and Space key for accessibility
      if (e.key === 'Enter' || e.key === ' ') {
        if (!url) return;

        if (shouldHandleInternally(url)) {
          e.preventDefault();
          navigate();
        }
        // For external links, let the browser handle the default behavior
        // This preserves native behavior like Cmd+Enter for new tabs
      }
    },
    [url, shouldHandleInternally, navigate]
  );

  return {
    handleClick,
    handleKeyDown,
    navigate,
    normalizedUrl,
  };
};
